import NiceModal from '@ebay/nice-modal-react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { FC, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styles from '../search.module.scss'
import { FilterCategory } from '@/components/search/FilterCategory'
import { FilterPackage } from '@/components/search/FilterPackage'
import { Alloys } from '@/components/search/filters/Alloys'
import { AvailableMultiSelect } from '@/components/search/filters/AvailableMultiSelect'
import { AvailableVehiclePriceTagsMultiSelect } from '@/components/search/filters/AvailableVehiclePriceTagsMultiSelect'
import { Capacity } from '@/components/search/filters/Capacity'
import { ComfortAndSafety } from '@/components/search/filters/ComfortAndSafety'
import { Convenience } from '@/components/search/filters/Convenience'
import { ElectricFilterMinMax } from '@/components/search/filters/ElectricFilterMinMax'
import { EngineSize } from '@/components/search/filters/EngineSize'
import { Features } from '@/components/search/filters/Features'
import { InitialPayment } from '@/components/search/filters/InitialPayment'
import { LeaseType } from '@/components/search/filters/LeaseType'
import { LegacyMultiSelect } from '@/components/search/filters/LegacyMultiSelect'
import { Manufacturers } from '@/components/search/filters/Manufacturers'
import { Models } from '@/components/search/filters/Models'
import { MonthlyPrice } from '@/components/search/filters/MonthlyPrice'
import { SortBy } from '@/components/search/filters/SortBy'
import { TextSearch } from '@/components/search/inputs/TextSearch'
import { Toggle } from '@/components/search/inputs/Toggle'
import { LoadTrigger } from '@/components/search/saved-search/LoadTrigger'
import { SaveTrigger } from '@/components/search/saved-search/SaveTrigger'
import { Button, ButtonHierarchy } from '@/components/styleguide/Button'
import {
  FILTER_BODY_STYLES,
  FILTER_BOOT_SPACE,
  FILTER_CAPACITY,
  FILTER_DELIVERY,
  FILTER_DOORS,
  FILTER_DRIVE_TYPES,
  FILTER_ELECTRIC_BATTERY_CAPACITY,
  FILTER_ELECTRIC_FASTEST_CHARGE_RATE,
  FILTER_ELECTRIC_HOME_CHARGE_TIME,
  FILTER_ELECTRIC_RANGE,
  FILTER_FEATURES,
  FILTER_FEATURES_CAB,
  FILTER_FEATURES_FLATBED,
  FILTER_FEATURES_SIDE_PANELS,
  FILTER_FUEL_TYPES,
  FILTER_INCLUSIVE_BENEFITS,
  FILTER_INITIAL_PAYMENT,
  FILTER_MANUFACTURERS,
  FILTER_MILEAGES,
  FILTER_MONTHLY_PAYMENT,
  FILTER_PERFORMANCE,
  FILTER_RANGES,
  FILTER_ROOF_HEIGHT,
  FILTER_SEATS,
  FILTER_SORT_BY,
  FILTER_SPEC,
  FILTER_TERMS,
  FILTER_TRANSMISSION_TYPES,
  FILTER_WHEELBASE,
} from '@/lib/constants'
import { isProd } from '@/lib/utilities/system'
import {
  clearAllSearchState,
  storeSearchMultiParameters,
  storeSearchParameters,
  storeUngroupedVehiclePriceTag,
  toggleGroupedVehiclePriceTag,
} from '@/store/reducers/search'
import { selectCountOfSearchParameters, selectSearchUpdate } from '@/store/selectors/search'
import { selectIsAuthenticated } from '@/store/selectors/user'
import { Size } from '@/types/system'

const MultiSelect = dynamic(() => import('@/components/search/filters/MultiSelect'), { ssr: false })
const Select = dynamic(() => import('@/components/search/filters/Select'), { ssr: false })
const SelectFromRange = dynamic(() => import('@/components/search/inputs/SelectFromRange'), {
  ssr: false,
})
const AuthenticationModal = dynamic(() => import('@/components/modals/AuthenticationModal'), {
  ssr: false,
})

type SearchFiltersProps = {
  isMobile: boolean
  isVans?: boolean
  setIsMobileFiltersOpen?: (value: boolean) => void
  visibleFilters: string[]
  setVisibleFilters: (value: string[]) => void
  toggleFilter: (id: string) => void
  toggleId: string
  setToggleId: (id: string) => void
}

export const SearchFilters = ({
  isMobile,
  setIsMobileFiltersOpen,
  setVisibleFilters,
  toggleFilter,
  toggleId,
  setToggleId,
  isVans = false,
  visibleFilters = [],
}: SearchFiltersProps) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const filterCount = useSelector(selectCountOfSearchParameters)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const search = useSelector(selectSearchUpdate)
  const prevSearchRef = useRef(search)

  useEffect(() => {
    if (prevSearchRef.current !== search) {
      if (toggleId && !visibleFilters.includes(toggleId)) {
        setVisibleFilters(prev => [...prev, toggleId])
        setToggleId('')
      }
      prevSearchRef.current = search
    }
  }, [search, toggleId, visibleFilters, setVisibleFilters, setToggleId])

  const removeDuplicatePrefixFilters = (array: string[]): string[] => {
    const prefixMap: { [prefix: string]: number } = {}

    return array.filter(filter => {
      const [prefix] = filter.split('-')
      if (prefixMap[prefix] === undefined) {
        prefixMap[prefix] = 1
        return true
      } else {
        prefixMap[prefix]++
        return false
      }
    })
  }

  const onFilterChange = ({
    type,
    filter,
    value,
    isGrouped,
    vehiclePriceTag,
  }:
    | {
        type: 'single' | 'multi' | 'vehiclePriceTag'
        filter?: string
        value?: string | number | (string | number)[]
        isGrouped?: boolean
        vehiclePriceTag?: {
          category: number
          tag?: number | undefined
        }
      }
    | {
        type: 'vehiclePriceTag'
        isGrouped: boolean
        filter?: string
        vehiclePriceTag: {
          category: number
          tag?: number | undefined
        }
        value?: string | number | (string | number)[]
      }
    | {
        type: 'single' | 'multi'
        isGrouped?: boolean
        filter: string
        vehiclePriceTag?: {
          category: number
          tag?: number | undefined
        }
        value: string | number | (string | number)[]
      }) => {
    const visibleFiltersWithoutDuplicates = removeDuplicatePrefixFilters(visibleFilters)
    if (type === 'multi') {
      dispatch(
        storeSearchMultiParameters({
          filter,
          value,
          visibleFilters: visibleFiltersWithoutDuplicates,
        }),
      )
    } else if (type === 'single') {
      dispatch(
        storeSearchParameters({
          filter,
          value,
          visibleFilters: visibleFiltersWithoutDuplicates,
        }),
      )
    } else if (type === 'vehiclePriceTag') {
      if (isGrouped) {
        dispatch(
          toggleGroupedVehiclePriceTag({
            vehiclePriceTagCategoryId: vehiclePriceTag?.category,
            visibleFilters: visibleFiltersWithoutDuplicates,
          }),
        )
      } else {
        dispatch(
          storeUngroupedVehiclePriceTag({
            visibleFilters: visibleFiltersWithoutDuplicates,
            category: vehiclePriceTag?.category,
            tag: vehiclePriceTag?.tag,
          }),
        )
      }
    }

    if (router.asPath.includes('page=')) {
      const isVans = router.asPath.includes('van-leasing')

      router.push(`/${isVans ? 'van' : 'car'}-leasing/search`, undefined, {
        scroll: false,
      })
    }

    const searchToolbar = document.querySelector('.search__container')

    // only scrolls if below toolbar
    if (searchToolbar?.getBoundingClientRect()?.y < 0) {
      searchToolbar?.scrollIntoView({
        behavior: 'smooth',
        inline: 'start',
      })
    }
  }

  const generateFilterProps = (filter: string, typeName?: string) => {
    const filterName = `${filter}${typeName ? `-${typeName}` : ''}`
    return {
      onChange: onFilterChange,
      filter,
      toggleFilter: () => toggleFilter(filterName),
      isFilterOpen: visibleFilters.includes(filterName),
      typeName,
    }
  }

  return (
    <div className={styles.filters}>
      {!isMobile && (
        <div className={styles.header}>
          <p className={styles.title}>Filters</p>
          {filterCount > 0 && (
            <Button
              size={Size.Small}
              hasBackground={false}
              hierarchy={ButtonHierarchy.Borderless}
              hasUnderline
              label={`Clear (${filterCount})`}
              onClick={() => {
                setVisibleFilters([])
                dispatch(clearAllSearchState())
              }}
            />
          )}
        </div>
      )}
      <FilterPackage>
        <FilterCategory hasBorderBottom={!isMobile}>
          {isAuthenticated ? (
            <div className={styles['saved-search-container']}>
              <LoadTrigger setIsMobileFiltersOpen={setIsMobileFiltersOpen} />
              {!isMobile && <SaveTrigger hierarchy={ButtonHierarchy.Filled} />}
            </div>
          ) : (
            <Button
              className={`${styles['guest-save-search']} ${!isProd() ? ' search__login-button' : ''}`}
              onClick={() => {
                if (setIsMobileFiltersOpen) {
                  setIsMobileFiltersOpen(false)
                }
                NiceModal.show(AuthenticationModal as FC)
              }}
              label={
                <span>
                  <span className={styles.desktop}>Log in to save</span>
                  <span className={styles.mobile}>Save</span>
                  <span>search</span>
                </span>
              }
              hierarchy={ButtonHierarchy.Bezeled}
              icon="ArrowRight"
              size={Size.Large}
              iconPosition="right"
            />
          )}
        </FilterCategory>
        {!isMobile && (
          <FilterCategory hasBorderBottom>
            <TextSearch
              visibleFilters={visibleFilters}
              isVans={isVans}
              isMobile={false}
            />
          </FilterCategory>
        )}
        {!isVans && (
          <FilterCategory hasBorderBottom>
            <LeaseType isMobile={isMobile} />
          </FilterCategory>
        )}
        <FilterCategory hasBorderBottom={isMobile}>
          <Toggle
            activeFilterValue={1}
            inactiveFilterValue={0}
            parameter="inStock"
            onChange={onFilterChange}
            title="In stock only"
            subtitle="Only see vehicles that are available for quick delivery."
            className={!isProd() ? ' search__in-stock-toggle' : ''}
          />
          <Toggle
            activeFilterValue={3}
            inactiveFilterValue={1}
            parameter="grouping"
            title="Group vehicle by model"
            subtitle="Only show the best deal for each model."
            onChange={onFilterChange}
            className={!isProd() ? ' search__group-model-toggle' : ''}
          />
        </FilterCategory>
        {isMobile && (
          <FilterCategory hasBorderBottom>
            <SortBy {...generateFilterProps(FILTER_SORT_BY)} />
          </FilterCategory>
        )}
      </FilterPackage>
      <FilterPackage>
        <FilterCategory header="Lease details">
          <MonthlyPrice
            {...generateFilterProps(FILTER_MONTHLY_PAYMENT)}
            isMobile={isMobile}
          />
          <InitialPayment
            {...generateFilterProps(FILTER_INITIAL_PAYMENT)}
            isMobile={isMobile}
          />
          <AvailableMultiSelect
            title="Mileage per annum"
            hasAbbreviatedLabels
            {...generateFilterProps(FILTER_MILEAGES)}
          />
          <AvailableMultiSelect
            title="Term"
            hasAbbreviatedLabels
            {...generateFilterProps(FILTER_TERMS)}
          />
          <AvailableVehiclePriceTagsMultiSelect
            title="Delivery time"
            {...generateFilterProps(FILTER_DELIVERY)}
          />
        </FilterCategory>
      </FilterPackage>
      <FilterPackage>
        <FilterCategory header="Make & model">
          <Manufacturers {...generateFilterProps(FILTER_MANUFACTURERS)} />
          <Models
            title="Model"
            {...generateFilterProps(FILTER_RANGES)}
          />
        </FilterCategory>
      </FilterPackage>
      <FilterPackage>
        <FilterCategory header="Fuel & drive">
          <LegacyMultiSelect
            title="Fuel"
            {...generateFilterProps(FILTER_FUEL_TYPES)}
          />
          <LegacyMultiSelect
            title="Transmission"
            {...generateFilterProps(FILTER_TRANSMISSION_TYPES)}
          />
          <LegacyMultiSelect
            title="Drive"
            maximumNumberOfVisibleSelectedOptions={1}
            hasAbbreviatedLabels
            {...generateFilterProps(FILTER_DRIVE_TYPES)}
          />
        </FilterCategory>
      </FilterPackage>
      <FilterPackage>
        <FilterCategory header="Body type & accessibility">
          <LegacyMultiSelect
            title="Body type"
            {...generateFilterProps(FILTER_BODY_STYLES)}
          />
          <LegacyMultiSelect
            title="Seats"
            {...generateFilterProps(FILTER_SEATS)}
          />
          <LegacyMultiSelect
            title="Doors"
            {...generateFilterProps(FILTER_DOORS)}
          />
          {!isVans && (
            <MultiSelect
              title="Bootspace"
              {...generateFilterProps(FILTER_BOOT_SPACE)}
            />
          )}
        </FilterCategory>
      </FilterPackage>
      {!isVans && (
        <FilterPackage>
          <FilterCategory header="Battery">
            <SelectFromRange
              title="Battery range"
              type="min"
              supportingText={{
                title: 'This is roughly how far the car can drive on a single full charge.',
                subtitle: 'According to WLTP (Worldwide Harmonized Light Vehicles Test Procedure)',
              }}
              {...generateFilterProps(FILTER_ELECTRIC_RANGE)}
            />
            <ElectricFilterMinMax
              title="Fastest charge rate"
              {...generateFilterProps(FILTER_ELECTRIC_FASTEST_CHARGE_RATE)}
            />
            <SelectFromRange
              title="Home charge time"
              type="max"
              hasAbbreviatedLabels
              supportingText={{
                title:
                  'This is how long the car will take to fully charge using a charging point (7.4kW)',
              }}
              {...generateFilterProps(FILTER_ELECTRIC_HOME_CHARGE_TIME)}
            />
            <ElectricFilterMinMax
              title="Battery size"
              {...generateFilterProps(FILTER_ELECTRIC_BATTERY_CAPACITY)}
            />
          </FilterCategory>
        </FilterPackage>
      )}
      <FilterPackage>
        <FilterCategory header="Engine">
          <Select
            title="CO2"
            {...generateFilterProps(FILTER_PERFORMANCE, 'MAX_CO2')}
          />
          <Select
            title="MPG"
            {...generateFilterProps(FILTER_PERFORMANCE, 'MIN_MPG')}
          />
          <EngineSize {...generateFilterProps(FILTER_PERFORMANCE, 'MAX_ENGINE')} />
        </FilterCategory>
      </FilterPackage>
      <FilterPackage>
        <FilterCategory header="Additional specification">
          <Select
            title="0-62"
            {...generateFilterProps(FILTER_PERFORMANCE, 'MAX_MPH062')}
          />
          <Alloys {...generateFilterProps(FILTER_SPEC, 'ALLOYS')} />
          <ComfortAndSafety {...generateFilterProps(FILTER_SPEC, 'COMFORT')} />
          <Convenience {...generateFilterProps(FILTER_SPEC, 'CONVENIENCE')} />
        </FilterCategory>
      </FilterPackage>
      {isVans && (
        <FilterPackage>
          <FilterCategory header="Size & shape">
            <Capacity
              title="Capacity"
              {...generateFilterProps(FILTER_CAPACITY)}
            />
            <LegacyMultiSelect
              title="Wheelbase"
              hasAbbreviatedLabels
              {...generateFilterProps(FILTER_WHEELBASE)}
            />
            <LegacyMultiSelect
              title="Roof height"
              hasAbbreviatedLabels
              {...generateFilterProps(FILTER_ROOF_HEIGHT)}
            />
            <Features
              title="Cab"
              {...generateFilterProps(FILTER_FEATURES, FILTER_FEATURES_CAB)}
            />
            <Features
              title="Flatbed"
              {...generateFilterProps(FILTER_FEATURES, FILTER_FEATURES_FLATBED)}
            />
            <Features
              title="Side panels"
              {...generateFilterProps(FILTER_FEATURES, FILTER_FEATURES_SIDE_PANELS)}
            />
          </FilterCategory>
        </FilterPackage>
      )}
      <FilterPackage>
        <FilterCategory
          hasBorderBottom={false}
          header="Add ons">
          <AvailableVehiclePriceTagsMultiSelect
            title="Inclusive benefits"
            {...generateFilterProps(FILTER_INCLUSIVE_BENEFITS)}
          />
        </FilterCategory>
      </FilterPackage>
    </div>
  )
}
